import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, supabaseHelpers } from '../lib/supabase';
const useLocal = (import.meta.env?.VITE_USE_LOCAL_API === 'true') || (!import.meta.env?.VITE_SUPABASE_URL && !import.meta.env?.VITE_SUPABASE_ANON_KEY);

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Separate async operations object (required for Supabase auth safety)
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabaseHelpers?.getUserProfile(userId)
        if (!error && data) {
          setUserProfile(data)
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    },

    async update(updates) {
      if (!user?.id) return { error: 'Usuário não encontrado' }
      
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()

        if (!error && data) {
          setUserProfile(data)
          return { data, error: null }
        }
        return { data: null, error }
      } catch (error) {
        console.error('Profile update error:', error)
        return { data: null, error }
      } finally {
        setProfileLoading(false)
      }
    }
  }

  // Protected auth handlers (MUST remain synchronous)
  const authStateHandlers = {
    onChange: (event, session) => {
      if (useLocal) {
        const localUser = { id: 'guest', email: 'guest@local' };
        setUser(localUser);
        setLoading(false);
        profileOperations?.load(localUser?.id);
        return;
      }
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    if (useLocal) {
      authStateHandlers?.onChange(null, { user: { id: 'guest', email: 'guest@local' } });
      return;
    }
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // Listen for auth changes (CRITICAL: callback must remain synchronous)
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  const signUp = async (email, password, userData = {}) => {
    setLoading(true)
    try {
      const { data, error } = await supabaseHelpers?.signUp(email, password, userData)
      return { data, error }
    } catch (error) {
      console.error('Sign up error:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    setLoading(true)
    try {
      const { data, error } = await supabaseHelpers?.signIn(email, password)
      if (!error && data?.user && useLocal) {
        setUser(data?.user)
        profileOperations?.load(data?.user?.id)
      }
      return { data, error }
    } catch (error) {
      console.error('Sign in error:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    setLoading(true)
    try {
      const { error } = await supabaseHelpers?.signOut()
      if (!error && useLocal) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      console.error('Sign out error:', error)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  const signInWithGoogle = async () => {
    setLoading(true)
    try {
      const redirectTo = typeof window !== 'undefined' ? (window.location.origin + '/dashboard-overview') : undefined
      const { data, error } = await supabaseHelpers?.signInWithOAuth('google', { redirectTo })
      if (!error && data?.user && useLocal) {
        setUser(data?.user)
        profileOperations?.load(data?.user?.id)
      }
      return { data, error }
    } catch (error) {
      console.error('Google OAuth error:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile: profileOperations?.update,
    refreshProfile: () => user?.id && profileOperations?.load(user?.id)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext
